import { AllEnterpriseModule } from 'ag-grid-enterprise';
import UserStatusFilter, { type UserStatus } from '@/components/common/UserStatusFilter';
import ManagementTablePanel, { managementTableTheme } from '@/components/common/ManagementTablePanel';
import ColumnValuesFilter from '@/components/common/ColumnValuesFilter';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Pencil, Plus, Save, Trash2, X } from 'lucide-react';
import { AgGridReact } from 'ag-grid-react';
import {
  } from 'ag-grid-community';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import toast from 'react-hot-toast';
import { fetchInvestors, type Investor } from '@/api/investors';
import {
  createInvestorUser,
  deleteInvestorUser,
  fetchInvestorUsers,
  type InvestorUserListItem,
  type InvestorUserPayload,
  type InvestorUserRole,
  updateInvestorUser,
} from '@/api/investorUsers';
import { confirmToast, useErrorToast, LoadRetry } from '@/lib/notify';

const modules = [AllEnterpriseModule];

const theme = managementTableTheme;

interface InvestorUserModalState {
  mode: 'create' | 'edit';
  user?: InvestorUserListItem;
}

interface InvestorUserFormState {
  investor_uuid_id: string;
  full_name: string;
  email: string;
  password: string;
  role: InvestorUserRole;
  status: number;
}

function StatusBadge({ active }: { active: number }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
        active === 1 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
      }`}
    >
      {active === 1 ? 'Aktif' : 'Pasif'}
    </span>
  );
}

function RoleBadge({ role }: { role: InvestorUserRole }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
        role === 'admin' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'
      }`}
    >
      {role === 'admin' ? 'Admin' : 'Viewer'}
    </span>
  );
}

function InvestorUserModal({
  investors,
  state,
  onClose,
  onSaved,
}: {
  investors: Investor[];
  state: InvestorUserModalState;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<InvestorUserFormState>({
    investor_uuid_id: state.user?.investor_uuid_id ?? '',
    full_name: state.user?.full_name ?? '',
    email: state.user?.email ?? '',
    password: '',
    role: state.user?.role ?? 'viewer',
    status: state.user?.status ?? 1,
  });

  function setField<Key extends keyof InvestorUserFormState>(key: Key, value: InvestorUserFormState[Key]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    if (!form.investor_uuid_id || !form.email.trim() || !form.role) {
      toast.error('Yatırımcı, e-posta ve rol zorunludur.');
      return;
    }
    if (state.mode === 'create' && !form.password.trim()) {
      toast.error('Yeni kullanıcı için şifre zorunludur.');
      return;
    }

    const payload: InvestorUserPayload = {
      investor_uuid_id: form.investor_uuid_id,
      full_name: form.full_name.trim(),
      email: form.email.trim(),
      role: form.role,
      status: form.status,
      ...(form.password.trim() ? { password: form.password } : {}),
    };

    setSaving(true);
    try {
      if (state.mode === 'create') {
        await createInvestorUser(payload);
        toast.success('Yatırımcı kullanıcısı oluşturuldu.');
      } else {
        await updateInvestorUser(state.user!.investor_user_uuid_id, payload);
        toast.success('Yatırımcı kullanıcısı güncellendi.');
      }
      onSaved();
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 409) {
        toast.error('Bu yatırımcı için aynı e-posta zaten kayıtlı.');
      } else if (status === 404) {
        toast.error('Yatırımcı veya kullanıcı bulunamadı.');
      } else {
        toast.error(state.mode === 'create' ? 'Kullanıcı oluşturulamadı.' : 'Kullanıcı güncellenemedi.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between border-b pb-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {state.mode === 'create' ? 'Yeni Yatırımcı Kullanıcısı' : 'Yatırımcı Kullanıcısını Düzenle'}
            </h3>
            <p className="mt-1 text-sm text-gray-500">Kullanıcı yatırımcı paneline seçilen yatırımcı kapsamında erişir.</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        <form autoComplete="off" className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs font-medium text-gray-500">Yatırımcı</label>
            <select
              value={form.investor_uuid_id}
              onChange={(event) => setField('investor_uuid_id', event.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="">Yatırımcı seçin</option>
              {investors.map((investor) => (
                <option key={investor.investor_uuid_id} value={investor.investor_uuid_id}>
                  {investor.investor_name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">Ad Soyad</label>
            <input
              type="text"
              value={form.full_name}
              onChange={(event) => setField('full_name', event.target.value)}
              autoComplete="off"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">E-posta</label>
            <input
              type="email"
              value={form.email}
              onChange={(event) => setField('email', event.target.value)}
              autoComplete="off"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">Şifre</label>
            <input
              type="password"
              value={form.password}
              onChange={(event) => setField('password', event.target.value)}
              placeholder={state.mode === 'edit' ? 'Değiştirmek istemiyorsanız boş bırakın' : ''}
              autoComplete="new-password"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">Rol</label>
            <select
              value={form.role}
              onChange={(event) => setField('role', event.target.value as InvestorUserRole)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="viewer">Viewer</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs font-medium text-gray-500">Durum</label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setField('status', 1)}
                className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                  form.status === 1
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                    : 'border-gray-300 text-gray-500 hover:bg-gray-50'
                }`}
              >
                Aktif
              </button>
              <button
                type="button"
                onClick={() => setField('status', 0)}
                className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                  form.status === 0
                    ? 'border-rose-500 bg-rose-50 text-rose-700'
                    : 'border-gray-300 text-gray-500 hover:bg-gray-50'
                }`}
              >
                Pasif
              </button>
            </div>
          </div>
        </form>

        <div className="mt-6 flex justify-end gap-3 border-t pt-4">
          <button
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-50"
          >
            İptal
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-emerald-700 disabled:opacity-50"
          >
            <Save size={16} />
            Kaydet
          </button>
        </div>
      </div>
    </div>
  );
}

function ActionCell({
  data,
  onEdit,
  onDelete,
}: ICellRendererParams<InvestorUserListItem> & {
  onEdit: (user: InvestorUserListItem) => void;
  onDelete: (user: InvestorUserListItem) => void;
}) {
  if (!data) return null;
  return (
    <div className="flex h-full items-center justify-center gap-2">
      <button
        onClick={(event) => {
          event.stopPropagation();
          onEdit(data);
        }}
        className="rounded-lg p-1.5 text-blue-600 transition hover:bg-blue-50"
        title="Düzenle"
      >
        <Pencil size={16} />
      </button>
      <button
        onClick={(event) => {
          event.stopPropagation();
          onDelete(data);
        }}
        className="rounded-lg p-1.5 text-rose-600 transition hover:bg-rose-50"
        title="Sil"
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
}

function formatDate(value: string | null) {
  if (!value) return '—';
  return new Intl.DateTimeFormat('tr-TR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

export default function InvestorUsersTab() {
  const gridRef = useRef<AgGridReact<InvestorUserListItem>>(null);
  const [rowData, setRowData] = useState<InvestorUserListItem[]>([]);
  const [investors, setInvestors] = useState<Investor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useErrorToast(error);
  const [modalState, setModalState] = useState<InvestorUserModalState | null>(null);

  function loadData() {
    setLoading(true);
    setError('');
    Promise.all([fetchInvestorUsers(), fetchInvestors()])
      .then(([users, investorRows]) => {
        setRowData(users);
        setInvestors(investorRows);
      })
      .catch(() => setError('Yatırımcı kullanıcıları yüklenemedi.'))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadData();
  }, []);

  async function handleDelete(user: InvestorUserListItem) {
    if (!(await confirmToast('Kullanıcı silinsin mi?', user.email))) {
      return;
    }

    try {
      await deleteInvestorUser(user.investor_user_uuid_id);
      toast.success('Yatırımcı kullanıcısı silindi.');
      loadData();
    } catch {
      toast.error('Yatırımcı kullanıcısı silinemedi.');
    }
  }

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<UserStatus>('all');
  const activeCount = rowData.filter(row => row.status === 1).length;
  const filteredRows = useMemo(() => rowData.filter(row => statusFilter === 'all' || (row.status === 1) === (statusFilter === 'active')), [rowData, statusFilter]);

  const colDefs = useMemo<ColDef<InvestorUserListItem>[]>(
    () => [
      {
        field: 'full_name',
        headerName: 'Ad Soyad',
        flex: 1.4,
        minWidth: 180,
        pinned: 'left',
        filter: ColumnValuesFilter,
        valueFormatter: ({ value }: { value: string | null }) => value || '—',
      },
      {
        field: 'email',
        headerName: 'E-posta',
        flex: 1.7,
        minWidth: 220,
        filter: ColumnValuesFilter,
      },
      {
        field: 'investor_name',
        headerName: 'Yatırımcı',
        flex: 1.6,
        minWidth: 220,
        filter: ColumnValuesFilter,
      },
      {
        field: 'role',
        headerName: 'Rol',
        width: 120,
        filter: ColumnValuesFilter,
        filterParams: { valueLabel: (value: unknown) => value === 'admin' ? 'Admin' : 'Viewer' },
        cellRenderer: ({ value }: { value: InvestorUserRole }) => <RoleBadge role={value} />,
      },
      {
        field: 'status',
        headerName: 'Durum',
        width: 120,
        filterParams: { valueLabel: (value: unknown) => Number(value) === 1 ? 'Aktif' : 'Pasif' },
        filter: ColumnValuesFilter,
        cellRenderer: ({ value }: { value: number }) => <StatusBadge active={value} />,
      },
      {
        field: 'last_login_at',
        headerName: 'Son Giriş',
        minWidth: 170,
        filterParams: { valueLabel: (value: unknown) => formatDate(value == null ? null : String(value)) },
        valueFormatter: ({ value }: { value: string | null }) => formatDate(value),
      },
      {
        headerName: 'İşlemler',
        colId: 'actions',
        width: 120,
        sortable: false,
        filter: false,
        pinned: 'right',
        cellRenderer: (params: ICellRendererParams<InvestorUserListItem>) => (
          <ActionCell {...params} onEdit={(user) => setModalState({ mode: 'edit', user })} onDelete={handleDelete} />
        ),
      },
    ],
    [],
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: ColumnValuesFilter,
      resizable: true,
      floatingFilter: false,
      suppressHeaderMenuButton: false,
    }),
    [],
  );

  if (error) return <LoadRetry />;

  return (
    <div className="flex h-full flex-col gap-4">
      <ManagementTablePanel search={search} onSearch={setSearch} label="Yatırımcı kullanıcılarında ara" count={filteredRows.length} toolbar={<UserStatusFilter value={statusFilter} onChange={setStatusFilter} total={rowData.length} active={activeCount} />} actions={<button
          onClick={() => setModalState({ mode: 'create' })}
          className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-blue-700"
        >
          <Plus size={16} />
          Yeni
        </button>}>
        <div className="min-h-[300px] flex-1">
        <AgGridReact<InvestorUserListItem>
          ref={gridRef}
          modules={modules}
          theme={theme}
          quickFilterText={search}
          pagination
          paginationPageSize={100}
          paginationPageSizeSelector={[25, 50, 100]}
          rowData={loading ? null : filteredRows}
          columnDefs={colDefs}
          defaultColDef={defaultColDef}
          loading={loading}
          animateRows
          rowSelection="single"
          enableCellTextSelection
        />
        </div>
      </ManagementTablePanel>

      {modalState && (
        <InvestorUserModal
          investors={investors}
          state={modalState}
          onClose={() => setModalState(null)}
          onSaved={() => {
            setModalState(null);
            loadData();
          }}
        />
      )}
    </div>
  );
}
